/**
 * DCAT Compliance Dashboard
 * VERIFIED: Uses actual patterns from codebase
 */

import React, { useState, useEffect } from 'react';
import '../../styles/semantic.css';

interface DCATField {
  field_name: string;
  dcat_property: string;
  required: boolean;
  category: 'Required' | 'Recommended';
  description: string;
  coverage: number;
  datasets_with_value: number;
  total_datasets: number;
}

interface DCATComplianceData {
  overall_score: number;
  required_score: number;
  recommended_score: number;
  total_datasets: number;
  compliant_datasets: number;
  compliance_percentage: number;
  fields: DCATField[];
  recommendations: string[];
  metadata: {
    version: string;
    spec_url: string;
    w3c_standard: string;
    timestamp: string;
    analysis_date: string;
  };
}

export const DCATCompliance: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [complianceData, setComplianceData] = useState<DCATComplianceData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchDCATCompliance();
  }, []);

  const fetchDCATCompliance = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/semantic-interop/dcat-compliance', {
        headers: {
          'X-Organization-Id': getOrganizationId() || '',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch DCAT compliance data');
      }

      const data = await response.json();
      setComplianceData(data);
    } catch (err: unknown) {
      console.error('[DCATCompliance] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch DCAT compliance data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportDCAT = async () => {
    try {
      setExporting(true);

      const response = await fetch('/api/semantic-interop/export-dcat-rdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-Id': getOrganizationId() || '',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to export');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fair-compass-catalog-${new Date().toISOString().split('T')[0]}.ttl`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      console.error('[DCATCompliance] Export error:', err);
      alert(`Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="dcat-compliance-container">
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Analyzing DCAT compliance...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dcat-compliance-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading DCAT Compliance</h3>
          <p>{error}</p>
          <button className="btn-secondary" onClick={fetchDCATCompliance}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!complianceData || complianceData.total_datasets === 0) {
    return (
      <div className="dcat-compliance-container">
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No Datasets Found</h3>
          <p>Add datasets to your portfolio to begin DCAT compliance analysis.</p>
        </div>
      </div>
    );
  }

  const requiredFields = complianceData.fields.filter(f => f.required);
  const recommendedFields = complianceData.fields.filter(f => !f.required);
  const getScoreColor = (score: number) => {
    if (score >= 90) return '#10b981';
    if (score >= 80) return '#3b82f6';
    if (score >= 70) return '#f59e0b';
    return '#ef4444';
  };

  const handleOpenGuide = async () => {
    try {
      await fetch('/open-guide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guidePath: 'DCAT_Compliance_Dashboard.md' })
      });
    } catch (err) {
      console.error('[DCATCompliance] Error opening guide:', err);
    }
  };

  return (
    <div className="dcat-compliance-container">
      <div className="dcat-header">
        <div className="dcat-title-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h2>DCAT Compliance Dashboard</h2>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleOpenGuide();
              }}
              className="help-link"
              title="Open DCAT Compliance User Guide"
              style={{
                fontSize: '1.25rem',
                textDecoration: 'none',
                color: '#3b82f6',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dbeafe';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#eff6ff';
                e.currentTarget.style.borderColor = '#bfdbfe';
              }}
            >
              ?
            </a>
          </div>
          <p className="subtitle">DCAT-AP 2.1 (Data Catalog Vocabulary - Application Profile)</p>
        </div>
        <button
          className="btn-primary btn-export"
          onClick={handleExportDCAT}
          disabled={exporting}
        >
          {exporting ? (
            <>
              <span className="btn-spinner" />
              <span>Exporting...</span>
            </>
          ) : (
            <>
              <span>↓</span>
              <span>Export to DCAT-RDF</span>
            </>
          )}
        </button>
      </div>

      <div className="dcat-score-card">
        <div className="score-visual">
          <svg width="140" height="140" viewBox="0 0 140 140">
            <circle cx="70" cy="70" r="60" fill="none" stroke="#e2e8f0" strokeWidth="12" />
            <circle
              cx="70" cy="70" r="60" fill="none"
              stroke={getScoreColor(complianceData.overall_score)}
              strokeWidth="12"
              strokeDasharray={`${(complianceData.overall_score / 100) * 377} 377`}
              transform="rotate(-90 70 70)"
              strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 1s ease' }}
            />
            <text x="70" y="70" textAnchor="middle" dominantBaseline="middle" fontSize="32" fontWeight="600" fill="#1a202c">
              {Math.round(complianceData.overall_score)}%
            </text>
            <text x="70" y="95" textAnchor="middle" dominantBaseline="middle" fontSize="12" fill="#718096">
              Overall
            </text>
          </svg>
        </div>
        <div className="score-info">
          <h3>Portfolio DCAT Compliance</h3>
          <p className="score-summary">
            {complianceData.compliant_datasets} of {complianceData.total_datasets} datasets meet DCAT-AP 2.1 requirements
          </p>
          <div className="score-breakdown">
            <div className="score-breakdown-item">
              <span className="label">Required Fields:</span>
              <span className="value" style={{ color: getScoreColor(complianceData.required_score) }}>
                {Math.round(complianceData.required_score)}%
              </span>
            </div>
            <div className="score-breakdown-item">
              <span className="label">Recommended Fields:</span>
              <span className="value" style={{ color: '#718096' }}>
                {Math.round(complianceData.recommended_score)}%
              </span>
            </div>
          </div>
          <div className="score-status">
            {complianceData.overall_score >= 90 && (
              <span className="status-badge status-excellent">
                <span className="badge-icon">✓</span>
                Excellent Compliance
              </span>
            )}
            {complianceData.overall_score >= 80 && complianceData.overall_score < 90 && (
              <span className="status-badge status-strong">
                <span className="badge-icon">✓</span>
                Strong Compliance
              </span>
            )}
            {complianceData.overall_score >= 70 && complianceData.overall_score < 80 && (
              <span className="status-badge status-good">
                <span className="badge-icon">↗</span>
                Good Progress
              </span>
            )}
            {complianceData.overall_score < 70 && (
              <span className="status-badge status-needs-work">
                <span className="badge-icon">!</span>
                Needs Improvement
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="dcat-fields-section">
        <div className="section-header">
          <h3>Required DCAT Fields</h3>
          <span className="field-count">{requiredFields.length} fields</span>
        </div>
        <div className="fields-table">
          <div className="table-header">
            <div className="col-property">DCAT Property</div>
            <div className="col-description">Description</div>
            <div className="col-coverage">Coverage</div>
            <div className="col-status">Status</div>
          </div>
          {requiredFields.map((field) => (
            <div key={field.dcat_property} className="table-row">
              <div className="col-property">
                <code className="property-code">{field.dcat_property}</code>
              </div>
              <div className="col-description">{field.description}</div>
              <div className="col-coverage">
                <div className="coverage-bar-container">
                  <div className="coverage-bar">
                    <div
                      className="coverage-fill"
                      style={{
                        width: `${field.coverage}%`,
                        background: field.coverage >= 80 ? '#10b981' : field.coverage >= 50 ? '#f59e0b' : '#ef4444',
                      }}
                    />
                  </div>
                  <span className="coverage-text">
                    {field.datasets_with_value}/{field.total_datasets} ({Math.round(field.coverage)}%)
                  </span>
                </div>
              </div>
              <div className="col-status">
                {field.coverage === 100 && <span className="status-icon status-complete">✓</span>}
                {field.coverage >= 50 && field.coverage < 100 && <span className="status-icon status-partial">⚠</span>}
                {field.coverage < 50 && field.coverage > 0 && <span className="status-icon status-incomplete">!</span>}
                {field.coverage === 0 && <span className="status-icon status-missing">✕</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="dcat-fields-section">
        <div className="section-header">
          <h3>Recommended DCAT Fields</h3>
          <span className="field-count">{recommendedFields.length} fields</span>
        </div>
        <div className="fields-table">
          <div className="table-header">
            <div className="col-property">DCAT Property</div>
            <div className="col-description">Description</div>
            <div className="col-coverage">Coverage</div>
            <div className="col-status">Status</div>
          </div>
          {recommendedFields.map((field) => (
            <div key={field.dcat_property} className="table-row">
              <div className="col-property">
                <code className="property-code">{field.dcat_property}</code>
              </div>
              <div className="col-description">{field.description}</div>
              <div className="col-coverage">
                <div className="coverage-bar-container">
                  <div className="coverage-bar">
                    <div className="coverage-fill" style={{ width: `${field.coverage}%`, background: '#94a3b8' }} />
                  </div>
                  <span className="coverage-text">
                    {field.datasets_with_value}/{field.total_datasets} ({Math.round(field.coverage)}%)
                  </span>
                </div>
              </div>
              <div className="col-status">
                {field.coverage === 100 && <span className="status-icon status-complete">✓</span>}
                {field.coverage > 0 && field.coverage < 100 && <span className="status-icon status-partial">⚠</span>}
                {field.coverage === 0 && <span className="status-icon status-empty">−</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {complianceData.recommendations.length > 0 && (
        <div className="recommendations-panel">
          <div className="recommendations-header">
            <h3>Recommendations</h3>
            <span className="recommendations-count">{complianceData.recommendations.length} suggestions</span>
          </div>
          <ul className="recommendations-list">
            {complianceData.recommendations.map((rec, idx) => (
              <li key={idx} className="recommendation-item">
                <span className="rec-icon">💡</span>
                <span className="rec-text">{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="dcat-info-footer">
        <div className="info-section">
          <h4>About DCAT-AP 2.1</h4>
          <p>
            DCAT-AP is a W3C specification for data catalog interoperability.
            Version 2.1 is widely adopted in pharmaceutical organizations for integration
            with systems like Collibra, Alation, and Informatica.
          </p>
        </div>
        <div className="info-metadata">
          <p className="metadata-item"><strong>Specification:</strong> {complianceData.metadata.version}</p>
          <p className="metadata-item"><strong>Analysis Date:</strong> {complianceData.metadata.analysis_date}</p>
        </div>
        <div className="info-links">
          <a href={complianceData.metadata.w3c_standard} target="_blank" rel="noopener noreferrer" className="info-link">
            W3C DCAT Specification →
          </a>
          <a href={complianceData.metadata.spec_url} target="_blank" rel="noopener noreferrer" className="info-link">
            DCAT-AP Documentation →
          </a>
        </div>
      </div>
    </div>
  );
};

function getOrganizationId(): string | null {
  return null;
}

export default DCATCompliance;
